import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { Observable } from 'rxjs';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { WorkforceCashAdvance, WorkforceCashSettlement, WorkforcePerson } from './workforce-cash.models';
import { WorkforceCashService } from './workforce-cash.service';
import { WorkforceFinanceCurrencyOption, WorkforceFinanceCurrencyService } from './workforce-finance-currency.service';

@Component({ selector: 'app-workforce-cash-form', standalone: true, imports: [CommonModule, ReactiveFormsModule, RouterModule, ButtonModule, CheckboxModule, DatePickerModule, DialogModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, IftaLabelModule, SelectModule, TextareaModule], templateUrl: './workforce-cash-form.component.html', styleUrls: ['./workforce-cash-form.component.css'] })
export class WorkforceCashFormComponent implements OnInit, OnChanges {
  @Input() visible = true;
  @Input() embedded = false;
  @Input() kind?: 'advance' | 'settlement';
  @Input() record?: WorkforceCashAdvance | WorkforceCashSettlement;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter<WorkforceCashAdvance | WorkforceCashSettlement>();
  people: WorkforcePerson[] = []; currencyOptions: WorkforceFinanceCurrencyOption[] = []; saving = false; error = '';
  readonly categories = ['FUEL', 'TOLL', 'PARKING', 'LOADING_UNLOADING', 'MEALS', 'PER_DIEM', 'REPAIR_MINOR', 'MISCELLANEOUS'];
  form;
  constructor(private fb: FormBuilder, private route: ActivatedRoute, private router: Router, private service: WorkforceCashService, private session: SessionStorageService, private currencies: WorkforceFinanceCurrencyService) {
    this.form = this.fb.group({ fieldWorkerId: [null as number | null, Validators.required], currencyCode: ['SAR', [Validators.required, Validators.maxLength(3)]], shipmentId: [null as number | null], deliveryId: [null as number | null], fieldWorkPackageId: [null as number | null], fieldLaborAssignmentId: [null as number | null], logisticsProjectId: [null as number | null], requestedAmount: [null as number | null], paymentMethod: ['CASH'], paymentReference: [''], reason: [''], notes: [''], outOfPocket: [false], lines: this.fb.array([]) });
  }
  get companyId(): number | undefined { return this.session.getCompany()?.id; } get lines(): FormArray { return this.form.controls.lines; } get title(): string { return this.isSettlement ? 'New settlement' : 'New cash advance'; }
  get isSettlement(): boolean { return (this.kind || this.route.snapshot.data['kind']) === 'settlement'; }
  get isEdit(): boolean { return !!this.record; }
  get dialogTitle(): string { return this.isEdit ? this.isSettlement ? 'Edit settlement draft' : 'Edit cash advance draft' : this.title; }
  ngOnInit(): void {
    this.currencies.currencyOptions$.subscribe(options => this.currencyOptions = options);
    if (this.companyId) this.service.people(this.companyId).subscribe(rows => this.people = rows || []);
    this.route.queryParamMap.subscribe(params => {
      ['shipmentId', 'deliveryId', 'fieldWorkPackageId', 'fieldLaborAssignmentId', 'logisticsProjectId'].forEach(key => {
        const value = Number(params.get(key));
        if (value > 0) this.form.get(key)?.setValue(value);
      });
    });
    if (this.isSettlement) this.addLine(); else this.form.controls.requestedAmount.setValidators([Validators.required, Validators.min(0.01)]);
    this.patchRecord();
  }
  ngOnChanges(changes: SimpleChanges): void { if (changes['record'] && !changes['record'].firstChange) this.patchRecord(); }
  get operationalContext(): string {
    const value = this.form.getRawValue();
    if (value.deliveryId) return 'Delivery context attached';
    if (value.shipmentId) return 'Shipment context attached';
    if (value.fieldLaborAssignmentId) return 'Field labor assignment context attached';
    if (value.fieldWorkPackageId) return 'Field Work Package context attached';
    if (value.logisticsProjectId) return 'Project context attached';
    return 'No operational link — provide a reason for controlled ad-hoc work.';
  }
  addLine(value?: any): void { this.lines.push(this.fb.group({ workforceCashSettlementLineId: [value?.workforceCashSettlementLineId ?? null], expenseCategory: [value?.expenseCategory || 'FUEL', Validators.required], merchantName: [value?.merchantName || ''], spendDate: [value?.spendDate ? new Date(value.spendDate) : new Date()], description: [value?.description || ''], amount: [value?.amount ?? null, [Validators.required, Validators.min(0.01)]], taxAmount: [value?.taxAmount ?? 0], receiptRequired: [value?.receiptRequired || false], notes: [value?.notes || ''] })); }
  removeLine(index: number): void { this.lines.removeAt(index); }
  reset(): void { this.error = ''; this.form.reset({ fieldWorkerId: null, currencyCode: 'SAR', paymentMethod: 'CASH', outOfPocket: false }); this.lines.clear(); if (this.isSettlement) this.addLine(); this.patchRecord(); }
  onVisibleChange(visible: boolean): void { if (visible) this.visibleChange.emit(true); else this.close(); }
  close(): void { if (this.embedded) { this.visibleChange.emit(false); return; } this.router.navigate(['/accounts/enterprise/workforce-finance'], { queryParams: { tab: this.isSettlement ? 'SETTLEMENTS' : 'ADVANCES' } }); }
  save(): void {
    this.error = '';
    if (this.form.invalid || !this.companyId) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const actorId = this.session.getUser()?.id;
    const raw = this.form.getRawValue();
    const common = { owningCompanyId: this.companyId, createdBy: actorId, updatedBy: actorId };
    // The dialog deliberately shares its operational-context controls, but
    // each API resource accepts a distinct contract. Do not leak
    // settlement-only fields into an advance (or advance-only fields into a
    // settlement), otherwise strict REST deserialization rejects the request.
    const advanceValue = (() => {
      const { outOfPocket, lines, ...value } = raw;
      return { ...value, ...common } as WorkforceCashAdvance;
    })();
    const settlementValue = (() => {
      const { requestedAmount, paymentMethod, paymentReference, reason, ...value } = raw;
      return { ...value, ...common } as WorkforceCashSettlement;
    })();
    const value = this.isSettlement ? settlementValue : advanceValue;
    const request = (this.isSettlement
      ? this.record
        ? this.service.updateSettlement((this.record as WorkforceCashSettlement).workforceCashSettlementId!, value as WorkforceCashSettlement)
        : this.service.saveSettlement(value as WorkforceCashSettlement)
      : this.record
        ? this.service.updateAdvance((this.record as WorkforceCashAdvance).workforceCashAdvanceId!, value as WorkforceCashAdvance)
        : this.service.saveAdvance(value as WorkforceCashAdvance)) as Observable<WorkforceCashAdvance | WorkforceCashSettlement>;
    request.subscribe({
      next: (row: WorkforceCashAdvance | WorkforceCashSettlement) => {
        this.completed.emit(row);
        if (this.embedded) this.visibleChange.emit(false);
        else this.router.navigate(['/accounts/enterprise/workforce-finance', this.isSettlement ? 'settlements' : 'advances', this.isSettlement ? (row as WorkforceCashSettlement).workforceCashSettlementId : (row as WorkforceCashAdvance).workforceCashAdvanceId]);
      },
      error: (err: any) => {
        this.error = err?.error?.message || err?.message || 'The Workforce Finance cash record could not be saved.';
        this.saving = false;
      },
      complete: () => this.saving = false
    });
  }
  private patchRecord(): void { if (!this.record) return; this.form.patchValue(this.record); if (this.isSettlement) { this.lines.clear(); const recordLines = (this.record as WorkforceCashSettlement).lines || []; recordLines.forEach(line => this.addLine(line)); if (!recordLines.length) this.addLine(); } }
}
